import type { GeneratedDefinition } from "@keydials/api-client";

// ---------------------------------------------------------------------------
// The builder's autosave (KEY-560): one draft held in memory and saved whole, without the author ever pressing Save.
//
// The server replaces the whole latest draft on every save and gives it a new version id each time, so the session holds only
// the definition id and always sends the latest full model. Saves run one at a time; edits made while one is in flight are
// saved straight after it, as one request. Pure and timer-driven, so it can be tested without a screen.
// ---------------------------------------------------------------------------

/** What the dial calls an action point, both forms, as the server stores them. */
export interface NounForms {
  singular: string;
  plural: string;
}

export type SaveState = "idle" | "saving" | "saved" | "paused" | "error";

/** Why the builder can't carry on with this design at all. */
export type SessionEnd = "trialEnded" | "gone";

export interface DraftSnapshot {
  model: GeneratedDefinition;
  noun: NounForms | null;
  saveState: SaveState;
  /** The server's own sentence when it refused a save (paused), or null. */
  message: string | null;
  /** Set when the session is over: the trial ended, or the design is no longer the author's. */
  ended: SessionEnd | null;
  /** Set when the design moved on under the builder (sent for checking, or live), so editing here has stopped. */
  lockedLifecycle: "Checking" | "Published" | null;
  /** True while something typed hasn't reached the server. */
  unsaved: boolean;
  /** How many times the dial has reached the server. Readiness is read again each time this grows. */
  saves: number;
}

export interface DraftSessionDeps {
  save(model: GeneratedDefinition): Promise<void>;
  saveNoun(noun: NounForms): Promise<void>;
  /** Reads the design's current lifecycle, after a save was refused because it moved on. */
  readLifecycle(): Promise<string>;
  onChange(snapshot: DraftSnapshot): void;
}

export interface DraftSessionOptions {
  /** Save once edits have been quiet this long. */
  quietMs?: number;
  /** Save at least this often while edits keep coming. */
  maxWaitMs?: number;
  /** Retry delays after a failed save that wasn't a refusal. After the last one, the session waits for `retry()`. */
  backoffMs?: readonly number[];
}

export interface DraftSession {
  snapshot(): DraftSnapshot;
  /** Applies a change to the model and schedules a save. Ignored once the session has ended or locked. */
  edit(change: (model: GeneratedDefinition) => GeneratedDefinition): void;
  setNoun(noun: NounForms): void;
  /** Saves anything unsaved now. Resolves true when everything has reached the server. */
  flush(): Promise<boolean>;
  /** Tries again after the retries have run out. */
  retry(): void;
  /** Stops edits and saves while the design changes state (going live), so nothing queued meets it afterwards. */
  freeze(): void;
  /** Lets editing and saving carry on after a freeze. */
  thaw(): void;
  /** Stops editing because this builder moved the design on (sent it for checking). */
  lock(lifecycle: "Checking" | "Published"): void;
  /** Lets editing carry on once the design is a draft again, saving anything typed that was held back. */
  unlock(): void;
  /** Stops the timers. Call after a final flush. */
  dispose(): void;
}

const statusOf = (error: unknown): number | null => {
  const status = (error as { status?: unknown } | null)?.status;
  return typeof status === "number" ? status : null;
};

export function createDraftSession(
  initial: { model: GeneratedDefinition; noun: NounForms | null },
  deps: DraftSessionDeps,
  options: DraftSessionOptions = {},
): DraftSession {
  const quietMs = options.quietMs ?? 1000;
  const maxWaitMs = options.maxWaitMs ?? 4000;
  const backoffMs = options.backoffMs ?? [2000, 5000, 15000];

  let model = initial.model;
  let noun = initial.noun;
  let rev = 0;
  let savedRev = 0;
  let nounRev = 0;
  let savedNounRev = 0;
  let saves = 0;
  let saveState: SaveState = "idle";
  let message: string | null = null;
  let ended: SessionEnd | null = null;
  let lockedLifecycle: DraftSnapshot["lockedLifecycle"] = null;
  let failures = 0;
  let firstDirtyAt: number | null = null;
  let quietTimer: ReturnType<typeof setTimeout> | null = null;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let current: Promise<void> | null = null;
  let disposed = false;
  let frozen = false;

  const dirty = () => rev > savedRev || nounRev > savedNounRev;
  const stopped = () => ended !== null || lockedLifecycle !== null || frozen;

  const snapshot = (): DraftSnapshot => ({ model, noun, saveState, message, ended, lockedLifecycle, unsaved: dirty(), saves });
  const notify = () => {
    if (!disposed) deps.onChange(snapshot());
  };

  const clearTimers = () => {
    if (quietTimer) clearTimeout(quietTimer);
    if (retryTimer) clearTimeout(retryTimer);
    quietTimer = null;
    retryTimer = null;
  };

  function schedule() {
    if (stopped() || disposed) return;
    const now = Date.now();
    firstDirtyAt ??= now;
    if (quietTimer) clearTimeout(quietTimer);
    const delay = Math.max(0, Math.min(quietMs, firstDirtyAt + maxWaitMs - now));
    quietTimer = setTimeout(() => {
      quietTimer = null;
      void run();
    }, delay);
  }

  async function handleFailure(error: unknown) {
    const status = statusOf(error);
    if (status === 402) {
      ended = "trialEnded";
      clearTimers();
      return;
    }
    if (status === 404) {
      ended = "gone";
      clearTimers();
      return;
    }
    if (status === 409) {
      // Refused because the design moved on: out for checking, or live. Find out which rather than parsing the sentence.
      const lifecycle = await deps.readLifecycle().catch(() => null);
      lockedLifecycle = lifecycle === "Published" ? "Published" : "Checking";
      clearTimers();
      return;
    }
    if (status === 400) {
      // The server's own reason (a full plan, a payload it won't take). Nothing is retried until the author changes something.
      saveState = "paused";
      message = error instanceof Error ? error.message : null;
      return;
    }
    // A network failure or a server error: try again a few times, then wait to be asked.
    saveState = "error";
    message = null;
    failures += 1;
    if (failures <= backoffMs.length) {
      retryTimer = setTimeout(() => {
        retryTimer = null;
        void run();
      }, backoffMs[failures - 1]);
    }
  }

  function run(): Promise<void> {
    if (current) return current;
    if (!dirty() || stopped() || disposed) return Promise.resolve();

    current = (async () => {
      firstDirtyAt = null;
      saveState = "saving";
      message = null;
      notify();

      const sendingRev = rev;
      const sendingModel = model;
      const sendingNounRev = nounRev;
      const sendingNoun = noun;
      try {
        if (sendingRev > savedRev) {
          await deps.save(sendingModel);
          saves += 1;
        }
        savedRev = Math.max(savedRev, sendingRev);
        if (sendingNoun && sendingNounRev > savedNounRev) await deps.saveNoun(sendingNoun);
        savedNounRev = Math.max(savedNounRev, sendingNounRev);
        failures = 0;
        saveState = "saved";
      } catch (error) {
        await handleFailure(error);
      } finally {
        current = null;
      }
      notify();

      // Edits made during the save go straight after it, as one request.
      if (dirty() && saveState === "saved" && !stopped()) {
        await run();
      }
    })();
    return current;
  }

  return {
    snapshot,

    edit(change) {
      if (stopped() || disposed) return;
      model = change(model);
      rev += 1;
      if (saveState === "paused") {
        saveState = "idle";
        message = null;
      }
      notify();
      schedule();
    },

    setNoun(next) {
      if (stopped() || disposed) return;
      noun = next;
      nounRev += 1;
      notify();
      schedule();
    },

    async flush() {
      if (quietTimer) {
        clearTimeout(quietTimer);
        quietTimer = null;
      }
      while (current) await current;
      if (dirty() && !stopped() && saveState !== "paused") {
        await run();
      }
      while (current) await current;
      return !dirty() && !stopped();
    },

    retry() {
      if (stopped() || disposed || current) return;
      failures = 0;
      if (retryTimer) {
        clearTimeout(retryTimer);
        retryTimer = null;
      }
      void run();
    },

    freeze() {
      frozen = true;
      clearTimers();
    },

    thaw() {
      frozen = false;
      if (dirty() && !stopped() && !disposed) schedule();
    },

    lock(lifecycle) {
      lockedLifecycle = lifecycle;
      frozen = false;
      clearTimers();
      notify();
    },

    unlock() {
      lockedLifecycle = null;
      frozen = false;
      notify();
      if (dirty() && !disposed) schedule();
    },

    dispose() {
      disposed = true;
      clearTimers();
    },
  };
}
